import { useEffect, useMemo, useState } from 'react';
import { useInfiniteGetList } from 'react-admin';

import resourceNames from '../constants/resourceNames';
import { ChatSessionListItem } from '../components/pingu/types';

const SESSIONS_PER_PAGE = 5;

// Both the history sidebar and the spend popover read this list, so it is
// fetched whenever either of them is on screen.
const usePinguSessions = (enabled: boolean, isArchived = false) => {
  const [resetKey, setResetKey] = useState(0);
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPending,
    isRefetching,
    refetch,
  } = useInfiniteGetList<ChatSessionListItem>(
    resourceNames.PINGU_SESSIONS,
    {
      pagination: { page: 1, perPage: SESSIONS_PER_PAGE },
      sort: { field: 'createdAt', order: 'DESC' },
      filter: {
        isArchived,
        resetKey,
      },
    },
    { enabled },
  );

  const sessions = useMemo(() => data?.pages.flatMap((page) => page.data) || [], [data?.pages]);
  const pageCount = data?.pages.length || 0;

  useEffect(() => {
    if (!enabled || isArchived || !hasNextPage || isFetchingNextPage) return;
    fetchNextPage();
  }, [enabled, fetchNextPage, hasNextPage, isArchived, isFetchingNextPage]);

  const resetSessionsPagination = () => {
    setResetKey((previous) => previous + 1);
  };

  return {
    sessions,
    // A disabled query reports itself as pending forever, so only surface the
    // initial loading state while the list is actually wanted.
    sessionsLoading: enabled && isPending,
    sessionsRefreshing: enabled && isRefetching,
    sessionsLoadingMore: isFetchingNextPage,
    // A full page means there is older history the spend totals cannot see.
    sessionsCapped: isArchived && sessions.length >= SESSIONS_PER_PAGE,
    sessionsPageCount: pageCount,
    sessionsHasMore: hasNextPage,
    fetchMoreSessions: fetchNextPage,
    resetSessionsPagination,
    refetchSessions: refetch,
  };
};

export default usePinguSessions;
